<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageVideo from '@/components/common/BackstageVideo.vue';
import { Icons } from '@/models/icons.enum';
import type { SelfSellInVideoModel } from '@onrunning/backstage-models';
import type { SwiperContainer } from 'swiper/element/bundle';
import { computed, onBeforeUpdate, onMounted, ref } from 'vue';

export interface Props {
  videos: SelfSellInVideoModel[];
  startIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  startIndex: 0,
});

const emit = defineEmits<{
  close: [];
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const swiperRef = ref<SwiperContainer | null>(null);
const prevBtnRef = ref<HTMLButtonElement | null>(null);
const nextBtnRef = ref<HTMLButtonElement | null>(null);
const videoRefs = ref<InstanceType<typeof BackstageVideo>[]>([]);
const hasVideos = computed(() => props.videos.length > 0);
const showNavigation = computed(() => props.videos.length > 1);
const initialSlide = Math.min(Math.max(props.startIndex, 0), Math.max(props.videos.length - 1, 0));
const activeIndex = ref(initialSlide);
const isFirst = computed(() => activeIndex.value === 0);
const isLast = computed(() => activeIndex.value === props.videos.length - 1);

function onOverlayClick(event: MouseEvent) {
  if ((event.target as HTMLElement).classList.contains('video-popup-overlay')) {
    emit('close');
  }
}

function onSlideChange(event: CustomEvent) {
  const [swiper] = event.detail;
  const previousIndex = activeIndex.value;
  activeIndex.value = swiper.activeIndex;

  if (previousIndex !== activeIndex.value) {
    videoRefs.value[previousIndex]?.pauseVideo();
    videoRefs.value[activeIndex.value]?.playVideo();
  }
}

onBeforeUpdate(() => {
  videoRefs.value = [];
});

onMounted(() => {
  if (hasVideos.value) {
    dialogRef.value?.showModal();
  }

  // Swiper Element with init="false" requires all params as JS properties.
  // Vue attribute bindings are ignored when auto-init is disabled.
  const swiperEl = swiperRef.value;
  if (swiperEl) {
    Object.assign(swiperEl, {
      navigation: {
        prevEl: prevBtnRef.value,
        nextEl: nextBtnRef.value,
      },
      slidesPerView: 1,
      initialSlide,
      speed: 350,
    });
    swiperEl.initialize();
  }
});
</script>

<template>
  <Teleport to="body">
    <dialog
      v-if="hasVideos"
      ref="dialogRef"
      class="video-popup-overlay"
      data-cy="video-popup-overlay"
      @click="onOverlayClick"
      @cancel.prevent="emit('close')">
      <div class="video-popup" data-cy="video-popup" @click.stop>
        <swiper-container ref="swiperRef" init="false" class="video-popup__swiper" @swiperslidechange="onSlideChange">
          <swiper-slide v-for="(video, index) in videos" :key="index" class="video-popup__slide">
            <!--
              Mount all BackstageVideo components so that:
              - the active video autoplays on mount
              - the previous video pauses playback but keeps its state
            -->
            <BackstageVideo
              :ref="
                (el) => {
                  if (el) videoRefs[index] = el as InstanceType<typeof BackstageVideo>;
                }
              "
              :src="video.url"
              :poster="video.poster"
              :subtitles="video.subtitles"
              :title="video.title"
              object-fit="contain"
              :autoplay="index === initialSlide" />
          </swiper-slide>
        </swiper-container>

        <button
          v-if="showNavigation"
          ref="prevBtnRef"
          class="video-popup__nav video-popup__nav--prev"
          :disabled="isFirst"
          aria-label="Previous slide">
          <!-- Using flipped chevron-right because chevron-left has a different size in our icon set -->
          <BackstageIcon :name="Icons.CHEVRON_RIGHT" class="video-popup__nav-icon--flipped" />
        </button>

        <button
          v-if="showNavigation"
          ref="nextBtnRef"
          class="video-popup__nav video-popup__nav--next"
          :disabled="isLast"
          aria-label="Next slide">
          <BackstageIcon :name="Icons.CHEVRON_RIGHT" />
        </button>
      </div>
    </dialog>
  </Teleport>
</template>

<style lang="scss" scoped>
.video-popup-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  margin: 0;
  max-width: 100vw;
  max-height: 100vh;
  width: 100vw;
  height: 100vh;
  background: transparent;

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
  }
}

.video-popup {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border: none;
    border-radius: 50%;
    background-color: var(--primary-bg-color);
    color: var(--primary-text-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    transition:
      opacity 0.2s ease,
      background-color 0.2s ease;

    &--prev {
      left: 12px;
    }

    &--next {
      right: 12px;
    }

    :deep(i) {
      background-color: var(--primary-text-color);

      &.video-popup__nav-icon--flipped {
        transform: rotate(180deg);
      }
    }

    &:hover:not(:disabled) {
      background-color: var(--shade-2);
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  &__swiper {
    width: 80vw;
    max-width: 1276px;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background-color: var(--secondary-bg-color);

    @include breakpoint(small) {
      width: calc(100vw - 32px);
    }
  }
}
</style>
